// @ts-nocheck
/**
 * 大陸・深さ定義
 * @module twinKey/core/dungeon/data/continents
 */

// ============================================================
// 大陸定義
// 属性スコアに基づいて決定される世界
// ============================================================

export const CONTINENTS = {
  aviculus: {
    id: 'aviculus',
    name: '巨鳥の島アヴィクルス',
    description: '炎と光が支配する空の大陸',
    attributeBias: ['炎', '光'],
    backgrounds: ['密林', '火山', '神殿', '峠'],
    themeColor: '#ff6b6b',
  },
  black_curse: {
    id: 'black_curse',
    name: '黒呪の大陸',
    description: '闇と呪いが蔓延る大陸',
    attributeBias: ['闇', '水'],
    backgrounds: ['荒野', '沼地', '森', '塔'],
    themeColor: '#9d4ce6',
  },
  steam_boundary: {
    id: 'steam_boundary',
    name: '蒸気の境界',
    description: '炎と水が交わる混沌の地',
    attributeBias: ['炎', '水'],
    backgrounds: ['温泉', '間欠泉', '霧の谷', '蒸気坑道'],
    themeColor: '#6b9eff',
  },
  twilight_realm: {
    id: 'twilight_realm',
    name: '黄昏の境界',
    description: '光と闇が溶け合う世界',
    attributeBias: ['光', '闇'],
    backgrounds: ['境界線', '薄明の塔', '影の森', '残光の野'],
    themeColor: '#ffd700',
  },
  time_rift: {
    id: 'time_rift',
    name: '時空の狭間',
    description: '過去と未来が交錯する空間',
    attributeBias: ['未来'],
    backgrounds: ['遺跡', '時計塔', '虚無', '歪んだ回廊'],
    themeColor: '#00ffff',
  },
  primal_depths: {
    id: 'primal_depths',
    name: '原始の深淵',
    description: '太古の力が渦巻く原初の地',
    attributeBias: ['原始'],
    backgrounds: ['原始林', '地底湖', '結晶洞窟', '化石の墓場'],
    themeColor: '#6bff6b',
  },
  unknown: {
    id: 'unknown',
    name: '未知の領域',
    description: '属性の影響が薄い中立地帯',
    attributeBias: [],
    backgrounds: ['洞窟', '荒野', '森', '廃墟'],
    themeColor: '#888888',
  },
};

// ============================================================
// 深さ定義
// 設計書 v0.5 準拠: 6段階（20階刻み + 深淵の彼方）
// 階層計算式: 基礎階層(レアリティ) + 種別補正 + コスト補正 + シード変動
// ============================================================

export const DEPTH_LEVELS = {
  outer: {
    id: 'outer',
    name: '外縁部',
    floorRange: [10, 20],
    difficulty: '入門',
    enemyLevelRange: [1, 10],
    rarityThreshold: 4, // レアリティ合計 2-4 (C+C〜C+R)
    rewardMultiplier: 0.8,
    icon: '🌱',
  },
  shallow: {
    id: 'shallow',
    name: '浅層部',
    floorRange: [21, 40],
    difficulty: '初級',
    enemyLevelRange: [10, 25],
    rarityThreshold: 8, // レアリティ合計 5-8 (C+SR〜SR+SR)
    rewardMultiplier: 1.0,
    icon: '🌿',
  },
  medium: {
    id: 'medium',
    name: '中層部',
    floorRange: [41, 60],
    difficulty: '中級',
    enemyLevelRange: [25, 45],
    rarityThreshold: 12, // レアリティ合計 9-12 (SR+UR〜HR+HR)
    rewardMultiplier: 1.5,
    icon: '🌲',
  },
  deep: {
    id: 'deep',
    name: '深部',
    floorRange: [61, 80],
    difficulty: '上級',
    enemyLevelRange: [45, 70],
    rarityThreshold: 16, // レアリティ合計 13-16 (HR+SEC〜ALT+ALT)
    rewardMultiplier: 2.0,
    icon: '🌳',
  },
  abyss: {
    id: 'abyss',
    name: '最深部',
    floorRange: [81, 100],
    difficulty: '超級',
    enemyLevelRange: [70, 100],
    rarityThreshold: 20, // レアリティ合計 17-20 (ALT+SP〜GR+GR)
    rewardMultiplier: 3.0,
    icon: '🌑',
  },
  beyond: {
    id: 'beyond',
    name: '深淵の彼方',
    floorRange: [110, 110],
    difficulty: '究極',
    enemyLevelRange: [100, 150],
    rarityThreshold: Infinity, // GR×GR専用
    rewardMultiplier: 5.0,
    icon: '🕳️',
    description: 'GR×GR専用の真の最終ダンジョン',
  },
};

// ============================================================
// フロアタイプ定義
// ============================================================

export const FLOOR_TYPES = {
  battle: {
    id: 'battle',
    name: '戦闘',
    icon: '⚔️',
    description: '敵との戦闘',
  },
  strong_enemy: {
    id: 'strong_enemy',
    name: '強敵',
    icon: '💀',
    description: '強い敵、報酬2倍',
  },
  treasure: {
    id: 'treasure',
    name: '宝箱',
    icon: '📦',
    description: 'アイテム獲得',
  },
  event: {
    id: 'event',
    name: 'イベント',
    icon: '❓',
    description: '何かが起こる...',
  },
  trap: {
    id: 'trap',
    name: '罠',
    icon: '⚠️',
    description: '危険な気配...',
  },
  rest: {
    id: 'rest',
    name: '休憩',
    icon: '🏕️',
    description: 'HP30%回復',
  },
  boss: {
    id: 'boss',
    name: 'ボス',
    icon: '👹',
    description: 'ボス戦',
  },
};

// ============================================================
// ヘルパー関数
// ============================================================

/**
 * 大陸情報を取得
 * @param {string} continentId - 大陸ID
 * @returns {Object} 大陸情報
 */
export function getContinent(continentId) {
  return CONTINENTS[continentId] || CONTINENTS.unknown;
}

/**
 * 深さ情報を取得
 * @param {string} depthId - 深さID
 * @returns {Object} 深さ情報
 */
export function getDepthLevel(depthId) {
  return DEPTH_LEVELS[depthId] || DEPTH_LEVELS.outer;
}

/**
 * フロアタイプ情報を取得
 * @param {string} typeId - フロアタイプID
 * @returns {Object} フロアタイプ情報
 */
export function getFloorType(typeId) {
  return FLOOR_TYPES[typeId] || FLOOR_TYPES.battle;
}

/**
 * 全大陸の一覧を取得
 * @returns {Array} 大陸配列
 */
export function getAllContinents() {
  return Object.values(CONTINENTS);
}

/**
 * 全深さレベルの一覧を取得
 * @returns {Array} 深さ配列
 */
export function getAllDepthLevels() {
  return Object.values(DEPTH_LEVELS);
}
